import * as React from 'react';
import LinearProgress from '@mui/material/LinearProgress';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { styled } from '@mui/material/styles';
import notify from '../../../../../../../shared/feedback/notifications';
import { Translate } from '../../../../../../../shared/LanguageService';
import { AuthenticatedUser } from '../../../../../../../shared/types';
import { ACCEPTED_IMAGE_TYPES } from '../../uploadEditedImages/state/utils';
import { useReplaceImageMutation } from '../queries/use-mutation-replace-image';
import { ImageCornerButton } from './ImageCornerButton';

const DropFrame = styled('div', {
    shouldForwardProp: (prop) => prop !== 'isDragging' && prop !== 'isDropTarget'
})<{ isDragging: boolean; isDropTarget: boolean }>(({ theme, isDragging, isDropTarget }) => ({
    position: 'relative',
    flex: 1,
    display: 'flex',
    minHeight: 0,
    border: `6px dashed ${isDragging ? theme.palette.secondary.main : '#b8c1c1'}`,
    borderRadius: '6px',
    backgroundColor: '#dde3e3',
    overflow: 'hidden',
    transition: 'border-color 0.15s ease',
    cursor: isDropTarget ? 'copy' : 'default'
}));

const DropHint = styled('div')(({ theme }) => ({
    position: 'absolute',
    inset: 0,
    zIndex: 3,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(221, 227, 227, 0.85)',
    color: theme.palette.primary.main,
    pointerEvents: 'none'
}));

const UploadOverlay = styled('div')({
    position: 'absolute',
    inset: 0,
    zIndex: 3,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '0 20%',
    backgroundColor: 'rgba(221, 227, 227, 0.85)'
});

type EditedImageDropzoneProps = {
    orderId: number;
    imageId: number;
    user: AuthenticatedUser;
    canUpload: boolean;
    onReplaced: (newImageId: number) => void;
    children: (pickFileButton: React.ReactNode) => React.ReactNode;
};

export function EditedImageDropzone({
    orderId,
    imageId,
    user,
    canUpload,
    onReplaced,
    children
}: EditedImageDropzoneProps) {
    let [isDragging, setIsDragging] = React.useState(false);
    let fileInput = React.useRef<HTMLInputElement>(null);
    let replaceImage = useReplaceImageMutation({ orderId, imageId, user, onReplaced });
    let dropTitle = Translate('dropzone/title');

    function uploadFiles(files: FileList | File[]) {
        let candidates = Array.from(files);

        if (candidates.length !== 1) {
            notify.failure(
                Translate('image-processing/notification/you-can-only-upload-one-image-at-a-time')
            );
            return;
        }

        let [file] = candidates;

        if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
            notify.failure(`${file.name} ${Translate('dropzone/invalid-filetype')}`);
            return;
        }

        replaceImage.mutate(file);
    }

    function handleDrop(event: React.DragEvent) {
        event.preventDefault();
        setIsDragging(false);
        if (canUpload && !replaceImage.isLoading) {
            uploadFiles(event.dataTransfer.files);
        }
    }

    function handleDragOver(event: React.DragEvent) {
        if (!canUpload) {
            return;
        }
        event.preventDefault();
        setIsDragging(true);
    }

    function handleInputChange(event: React.ChangeEvent<HTMLInputElement>) {
        if (event.target.files) {
            uploadFiles(event.target.files);
        }
        event.target.value = '';
    }

    let pickFileButton = canUpload ? (
        <Tooltip placement="left" title={dropTitle}>
            <ImageCornerButton
                corner="right"
                aria-label={Translate('image-processing/edited/button-labels/upload-images')}
                disabled={replaceImage.isLoading}
                onClick={() => fileInput.current && fileInput.current.click()}
            >
                <CloudUploadIcon />
            </ImageCornerButton>
        </Tooltip>
    ) : null;

    return (
        <DropFrame
            isDragging={isDragging}
            isDropTarget={canUpload}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={() => setIsDragging(false)}
            data-testid={canUpload ? 'edited-image-dropzone' : undefined}
        >
            {children(pickFileButton)}
            {canUpload && (
                <input
                    ref={fileInput}
                    type="file"
                    accept={ACCEPTED_IMAGE_TYPES.join(',')}
                    style={{ display: 'none' }}
                    aria-label={dropTitle}
                    disabled={replaceImage.isLoading}
                    onChange={handleInputChange}
                />
            )}
            {isDragging && (
                <DropHint>
                    <Typography variant="body1" sx={{ fontSize: '24px' }}>
                        {dropTitle}
                    </Typography>
                </DropHint>
            )}
            {replaceImage.isLoading && (
                <UploadOverlay aria-live="polite" aria-busy="true">
                    <LinearProgress
                        aria-label={Translate(
                            'image-processing/edited/button-labels/upload-images'
                        )}
                        sx={{ width: '100%', height: '20px', borderRadius: '4px' }}
                    />
                </UploadOverlay>
            )}
        </DropFrame>
    );
}
